import {
  AllowOrderPassageCommand,
  AllowRestowOrderPassageCommand,
  AssignOrderToRailcarTrackPositionCommand,
  CarrierType,
  CarrierVisitDirection,
  ConfirmActualCargoAmountOrderCommand,
  CreateOrAssignOrderToRailcarTrackPositionCommand,
  CreateOrdersCommand,
  GetFirstAvailableOrderInGroupQueryItem,
  MarkOrderUpdatesAsReadCommand,
  OrderStatus,
  OrdersApi,
  PickUpOrderFilterType,
  RemoveOrFinishOpenOrdersCommand,
  ReportOrderShortlandedCommand,
  UpdateCustomerSequenceCommand,
  UpdateCustomsStatusCommand,
  UpdateOrderCommand,
  UpdateOrderStowagePositionCommand,
  UpdateOrderWeightCommand,
  UpdateReleaseOrdersCommand,
} from '@planning/app/api'
import { createApiClient } from '@planning/app/http-client'

class OrderService {
  httpClient = createApiClient(OrdersApi)

  update = async (cmd: UpdateOrderCommand) => await this.httpClient.put(cmd)

  allowPassage = async (cmd: AllowOrderPassageCommand) => await this.httpClient.allowPassage(cmd)

  allowRestowPassage = async (cmd: AllowRestowOrderPassageCommand) =>
    await this.httpClient.allowRestowPassage(cmd)

  /**
   * @deprecated use adminActionsService.deleteByIds instead
   */
  delete = async (id: number, deleteLinkedOrders?: boolean) =>
    await this.httpClient._delete(id, deleteLinkedOrders)

  removeOrFinishOpenOrders = async (
    removeOrFinishOpenOrdersCommand: RemoveOrFinishOpenOrdersCommand,
  ) => await this.httpClient.removeOrFinishOpenOrders(removeOrFinishOpenOrdersCommand)

  create = async (cmd: CreateOrdersCommand) => await this.httpClient.post(cmd)

  updateReleaseOrders = async (cmd: UpdateReleaseOrdersCommand) =>
    await this.httpClient.updateOrdersReleaseTime(cmd)

  getByContainerNumber = async (filter: string, status?: OrderStatus) => {
    const { data } = await this.httpClient.getByContainerNumber(filter, status)
    return data
  }

  getById = async (filter: number) => {
    const { data } = await this.httpClient.getById(filter)
    return data
  }

  getByIds = async (ids: number[]) => {
    const { data } = await this.httpClient.getByIds({ ids })
    return data
  }

  getByReferenceNumber = async (
    filter: string,
    direction: CarrierVisitDirection,
    carrierType?: CarrierType,
    status?: OrderStatus,
  ) => {
    const { data } = await this.httpClient.getByReferenceNumber(
      filter,
      direction,
      carrierType,
      status,
    )
    return data
  }

  dropOffOrders = async (filter?: string) => {
    const { data } = await this.httpClient.dropOffOrders(filter)

    return data
  }

  dropOffOrdersContainerJourney = async (filter?: string) => {
    const { data } = await this.httpClient.containerJourney(filter)

    return data
  }

  pickUpOrders = async (filter?: string, filterBy?: PickUpOrderFilterType) => {
    const { data } = await this.httpClient.pickUpOrders(filter, filterBy)

    return data
  }

  firstAvailableOrderInGroups = async (filter?: GetFirstAvailableOrderInGroupQueryItem[]) => {
    const { data } = await this.httpClient.firstAvailableOrderInGroups({ queryItems: filter })

    return data
  }

  updateWeight = async (cmd: UpdateOrderWeightCommand) => {
    await this.httpClient.weight(cmd.id, cmd)
  }
  updateStowagePosition = async (cmd: UpdateOrderStowagePositionCommand) => {
    await this.httpClient.updateStowagePosition(cmd.id, cmd)
  }

  updateCustomerSequence = async (cmd: UpdateCustomerSequenceCommand) => {
    await this.httpClient.customerSequence(cmd)
  }

  confirmActualCargoAmount = async (cmd: ConfirmActualCargoAmountOrderCommand) => {
    await this.httpClient.actualCargoAmount(cmd.id, cmd)
  }

  createOrAssignOrderToRailcarTrackPosition = async (
    cmd: CreateOrAssignOrderToRailcarTrackPositionCommand,
  ) => {
    const { data } = await this.httpClient.createOrAssignToRailcarTrackPosition(cmd)

    return data
  }

  assignToRailcarTrackPosition = async (cmd: AssignOrderToRailcarTrackPositionCommand) => {
    const { data } = await this.httpClient.assignToRailcarTrackPosition(cmd)

    return data
  }

  reportShortlandedContainer = async (cmd: ReportOrderShortlandedCommand) => {
    await this.httpClient.reportShortlandedContainer(cmd)
  }

  markOrderUpdatesAsRead = async (cmd: MarkOrderUpdatesAsReadCommand) => {
    await this.httpClient.markOrderUpdatesAsRead(cmd)
  }

  upsertCustomsReferences = async (cmd: UpdateCustomsStatusCommand) => {
    await this.httpClient.updateCustomsStatus(cmd)
  }

  getBookings = async (referenceNumber: string, operator: string, carrierVisitId: number) => {
    const { data } = await this.httpClient.getBookings(referenceNumber, operator, carrierVisitId)
    return data
  }

  validateBulkMove = async (
    containerNumbers: string[],
    destinationCarrierVisitId: number,
    direction: CarrierVisitDirection,
  ) => {
    const { data } = await this.httpClient.validateMoveOrdersToCarrierVisitQuery(
      containerNumbers,
      destinationCarrierVisitId,
      direction,
    )
    return data
  }
}

const orderService = new OrderService()

export default orderService
